/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parseBoolean } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

interface QuickFabProps extends WidgetProps {}

export interface QuickFabStyles {
    wrapper?: InjectableCss;
    quickFabWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    content?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const quickfabStyles: QuickFabStyles = {};

const styles = quickfabStyles;

type Props = QuickFabProps & HasHistory & HasProduct;

const QuickFab: React.FC<Props> = ({ product }) => {
    if (!product.properties.quickShipRelatedProduct && !product.properties.isQuickShip) {
        return null;
    }
    const quickFabLink = () => {
        if (product.properties?.parentChildUrlSegment && product.properties.parentChildUrlSegment.trim() !== "") {
            return `/product/${product.properties.parentChildUrlSegment}?option=${
                parseBoolean(product.properties.isQuickShip)
                    ? product.properties.normalShipRelatedProduct
                    : product.properties.quickShipRelatedProduct
            }`;
        }
        if (product.properties?.parentUrlSegment && product.properties.parentUrlSegment.trim() !== "") {
            return `/product/${product.properties.parentUrlSegment}`;
        }
        return `/product/${
            parseBoolean(product.properties.isQuickShip)
                ? product.properties.normalShipRelatedProduct
                : product.properties.quickShipRelatedProduct
        }`;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {product.properties.isQuickShip && parseBoolean(product.properties.isQuickShip) && (
                <StyledWrapper {...styles.quickFabWrapper}>
                    <Typography {...styles.title}>
                        {siteMessage("Custom_QuickFab_StandardShip_title", "Don’t need it expedited?")}
                    </Typography>
                    <Typography {...styles.content}>
                        {siteMessage(
                            "Custom_QuickFab_StandardShip_content",
                            "You can also choose our standard shiping option",
                        )}
                    </Typography>
                    <Link {...styles.link} href={quickFabLink()}>
                        {translate("View Standard Ship Option")}
                    </Link>
                </StyledWrapper>
            )}
            {product.properties.quickShipRelatedProduct && (
                <StyledWrapper {...styles.quickFabWrapper}>
                    <Typography {...styles.title}>
                        {siteMessage("Custom_Standard_QuickShip_title", "Need This Product Sooner?")}
                    </Typography>
                    <Typography {...styles.content}>
                        {siteMessage(
                            "Custom_Standard_QuickShip_content",
                            "QuickFabs by EIS provides prefabricated options with the same-day shipping.",
                        )}
                    </Typography>
                    <Link {...styles.link} href={quickFabLink()}>
                        {translate("View QuickFab Option")}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: withProduct(withHistory(QuickFab)),
    definition,
};

export default widgetModule;
